import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, FormattedNumber } from 'react-intl';
import { Row, Col, Button } from 'react-bootstrap';
import moment from 'moment';

import { clientSingleOptionFormatter } from 'components/forms/autocomplete/Client';
import { getCurrencySymbol } from 'helpers/financial';
import { getTimezoneFromState } from 'helpers/intl';
import { getCouponTypes } from 'helpers/order';

import EntityPropsUpdater from 'component/EntityPropsUpdater';
import Data from 'components/Data';

import FormInput from 'components/forms/FormInput';
import FormRichEditor from 'components/forms/FormRichEditor';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import DateTimePicker from 'components/forms/DateTimePicker';

import CouponBillingTargetEdit from 'components/modals/CouponBillingTargetEdit';
import { getCouponBalance } from 'actions/ListActions';
import Loader from 'components/layout/Loader';

import { addNotification } from 'actions/NotificationsActions';

class CouponData extends EntityPropsUpdater {
  constructor(props) {
    super(props);

    this.state = {
      showBillingTargetChange: false,
    };
  }

  componentDidUpdate = prevProps => {
    const {
      billingTargetFormStatus,
      couponBalance,
      doNotify,
      intl: { messages },
    } = this.props;

    if (
      prevProps.billingTargetFormStatus.pending &&
      !billingTargetFormStatus.pending &&
      !billingTargetFormStatus.hasError
    ) {
      this.closeBillingTargetChange();
    }

    if (prevProps.couponBalance.pending && !couponBalance.pending && !couponBalance.hasError) {
      doNotify(1, messages.coupon_balance_success);
    }
  };

  closeBillingTargetChange = () => {
    this.setState({ showBillingTargetChange: false });
  };

  getInvoicingTypes = () => {
    const {
      intl: { messages },
    } = this.props;
    return [
      { label: messages.instantly, value: 'instantly' },
      { label: messages.monthly, value: 'monthly' },
      { label: messages.quarterly, value: 'quarterly' },
      { label: messages.annual, value: 'annual' },
    ];
  };

  getRechargeableTypes = () => {
    const {
      intl: { messages },
    } = this.props;
    return [
      { label: messages.coupon_never, value: 'unset' },
      { label: messages.monthly, value: 'monthly' },
      { label: messages.quarterly, value: 'quarterly' },
      { label: messages.annual, value: 'annual' },
    ];
  };

  getPrintTypes = () => {
    const {
      intl: { messages },
    } = this.props;
    return [{ label: messages.coupon_unset, value: 'unset' }, { label: messages.coupon_print_pdf, value: 'pdf_only' }];
  };

  render() {
    const {
      intl: { messages },
      appIntl,
      entityLoaded,
      entity,
      disabled,
      defaultTimezone,
      inModal,
      billingTarget,
    } = this.props;
    const { showBillingTargetChange } = this.state;

    const currencySymbol = getCurrencySymbol((entityLoaded && entity.getCurrency()) || 'EUR');
    const minDate = moment(entityLoaded && entity.getCdate());

    const billingTargetValue = (!billingTarget.pending &&
      !!billingTarget.data &&
      clientSingleOptionFormatter(billingTarget.data)) || { value: null, label: messages.unassigned };
    return (
      <div className="clearfix">
        {entityLoaded && (
          <Row>
            <Data dataName="billingTarget" url={`/api/coupondefinitions/${entity.getId()}/billingtarget.json`} />

            <Col xs={12} md={inModal ? 12 : 6}>
              <div className="panel panel-transparent">
                <div className="panel-body">
                  <p>{messages.formGroup.basic_information}</p>
                  <div className="form-group-attached">
                    <FormInput
                      label={messages.name}
                      name="name"
                      value={entity.getName()}
                      meta={entity.getNameMetaInformation()}
                      validation={entity.getNameValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                    />
                    <FormRichEditor
                      label={messages.description}
                      name="description"
                      value={entity.getDescription()}
                      meta={entity.getDescriptionMetaInformation()}
                      validation={entity.getDescriptionValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                    />
                    <FormRichEditor
                      name="notes"
                      label={messages.note}
                      value={entity.getNotes()}
                      meta={entity.getNotesMetaInformation()}
                      validation={entity.getNotesValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    {/* Todo If we work with different currencies at some point, the controller and these lines must be adjusted */}
                    <p className="m-t-10">{messages.coupon_balance_title}</p>
                    <p>
                      <span style={{ fontWeight: '600' }}>{messages.total_coupon_budget} </span>
                      <span className="badge badge-secondary">
                        <FormattedNumber value={entity.getBudget()} style="currency" currency="EUR" />
                      </span>
                    </p>
                    <p>
                      <span style={{ fontWeight: '600' }}>{messages.spent_coupon_budget} </span>
                      {this.props.couponBalance.value !== undefined && (
                        <span className="badge badge-danger">
                          <FormattedNumber value={this.props.couponBalance.value} style="currency" currency="EUR" />
                        </span>
                      )}
                    </p>
                    <p>
                      <span style={{ fontWeight: '600' }}>{messages.remaining_coupon_budget} </span>
                      {this.props.couponBalance.value !== undefined && (
                        <>
                          <span className="badge badge-warning">
                            <FormattedNumber
                              value={entity.getBudget() - this.props.couponBalance.value}
                              style="currency"
                              currency="EUR"
                            />
                          </span>
                        </>
                      )}
                    </p>
                    <Button
                      className="m-t-5 m-b-5 btn-rounded"
                      bsStyle="warning"
                      onClick={() => this.props.getCouponBalance(entity.getId())}
                    >
                      {messages.get_coupon_balance}
                    </Button>
                    <>{this.props.couponBalance?.pending && <Loader />}</>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={12} md={inModal ? 12 : 6}>
              <div className="panel panel-transparent">
                <div className="panel-body">
                  <p>{messages.financial}</p>
                  <div className="form-group-attached">
                    <Row>
                      <Col xs={6}>
                        <FormAutocomplete
                          name="type"
                          label={messages.type}
                          options={getCouponTypes(messages)}
                          value={entity.getType()}
                          meta={entity.getTypeMetaInformation()}
                          validation={entity.getTypeValidationResult()}
                          onChange={this.updateEntity}
                          disabled={disabled}
                        />
                      </Col>
                      <Col xs={6}>
                        <FormAutocomplete
                          name="printType"
                          label={messages.coupon_print_type}
                          options={this.getPrintTypes()}
                          value={entity.getPrintType()}
                          meta={entity.getPrintTypeMetaInformation()}
                          validation={entity.getPrintTypeValidationResult()}
                          onChange={this.updateEntity}
                          disabled={disabled}
                        />
                      </Col>
                    </Row>
                    <FormInput
                      prefixAddon={currencySymbol}
                      name="budget"
                      label={messages.budget_value}
                      value={entity.getBudget()}
                      meta={entity.getBudgetMetaInformation()}
                      validation={entity.getBudgetValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                      type="number"
                    />
                    <Row>
                      <Col xs={6}>
                        <DateTimePicker
                          name="dateFrom"
                          label={messages.date_from}
                          value={entity.getDateFrom()}
                          minDate={minDate}
                          locale={appIntl.locale}
                          displayTz={defaultTimezone}
                          onChange={this.updateEntity}
                          disabled={disabled}
                        />
                      </Col>
                      <Col xs={6}>
                        <DateTimePicker
                          name="dateTo"
                          label={messages.date_to}
                          value={entity.getDateTo()}
                          minDate={minDate}
                          locale={appIntl.locale}
                          displayTz={defaultTimezone}
                          onChange={this.updateEntity}
                          showClearDate
                          placeholder={messages.without_end_date}
                          disabled={disabled}
                        />
                      </Col>
                    </Row>
                  </div>

                  <p className="m-t-15">{messages.calculations}</p>
                  <div className="form-group-attached">
                    {entity.getType() === 'percentage' && (
                      <FormInput
                        suffixAddon="%"
                        name="percentDiscount"
                        label={messages.percent_discount_value}
                        value={entity.getPercentDiscount()}
                        meta={entity.getPercentDiscountMetaInformation()}
                        validation={entity.getPercentDiscountValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                        type="number"
                      />
                    )}
                    {entity.getType() === 'monetary' && (
                      <FormInput
                        prefixAddon={currencySymbol}
                        name="valueDiscount"
                        label={messages.monetary_discount_value}
                        value={entity.getValueDiscount()}
                        meta={entity.getValueDiscountMetaInformation()}
                        validation={entity.getValueDiscountValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                        type="number"
                      />
                    )}
                    <FormInput
                      prefixAddon={currencySymbol}
                      name="maxCompanyBudget"
                      label={messages.company_budget}
                      value={entity.getMaxCompanyBudget()}
                      meta={entity.getMaxCompanyBudgetMetaInformation()}
                      validation={entity.getMaxCompanyBudgetValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                      type="number"
                      infoText={messages.enter_0_to_ignore_this_setting}
                    />
                    <FormInput
                      prefixAddon={currencySymbol}
                      name="maxUserBudget"
                      label={messages.user_budget}
                      value={entity.getMaxUserBudget()}
                      meta={entity.getMaxUserBudgetMetaInformation()}
                      validation={entity.getMaxUserBudgetValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                      type="number"
                      infoText={messages.enter_0_to_ignore_this_setting}
                    />
                    <FormInput
                      prefixAddon={currencySymbol}
                      name="minOrderValue"
                      label={messages.min_order_value}
                      value={entity.getMinOrderValue()}
                      meta={entity.getMinOrderValueMetaInformation()}
                      validation={entity.getMinOrderValueValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                      type="number"
                      infoText={messages.enter_0_to_ignore_this_setting}
                    />
                    <FormInput
                      prefixAddon={currencySymbol}
                      name="maxOrderDiscount"
                      label={messages.max_order_discount}
                      value={entity.getMaxOrderDiscount()}
                      meta={entity.getMaxOrderDiscountMetaInformation()}
                      validation={entity.getMaxOrderDiscountValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                      type="number"
                      infoText={messages.enter_0_to_ignore_this_setting}
                    />
                  </div>

                  <p className="m-t-15">{messages.billing_settings}</p>
                  <div className="form-group-attached">
                    <div className="form-group form-group-default padding-10">
                      <label>{messages.billing_target}</label>
                      <span>
                        {(billingTarget.pending && <i className="fa fa-circle-notch fa-spin" />) ||
                          billingTargetValue.label}
                      </span>
                      <Button
                        className="m-l-10 btn-rounded"
                        bsSize="xs"
                        onClick={() => this.setState({ showBillingTargetChange: true })}
                      >
                        {messages.edit}
                      </Button>
                    </div>
                    <FormInput
                      prefixAddon={currencySymbol}
                      name="subscriptionNetPrice"
                      infoText={messages.coupon_billing}
                      label={messages.billing_value}
                      value={entity.getSubscriptionNetPrice()}
                      meta={entity.getSubscriptionNetPriceMetaInformation()}
                      validation={entity.getSubscriptionNetPriceValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                      type="number"
                    />
                    <FormAutocomplete
                      name="invoicing"
                      label={messages.coupon_billing_period}
                      options={this.getInvoicingTypes()}
                      value={entity.getInvoicing()}
                      meta={entity.getInvoicingMetaInformation()}
                      validation={entity.getInvoicingValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                    />
                    <FormAutocomplete
                      name="rechargeable"
                      label={messages.coupon_recharge_period}
                      options={this.getRechargeableTypes()}
                      value={entity.getRechargeable()}
                      meta={entity.getRechargeableMetaInformation()}
                      validation={entity.getRechargeableValidationResult()}
                      onChange={this.updateEntity}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            </Col>

            <CouponBillingTargetEdit
              show={showBillingTargetChange}
              couponId={entity.getId()}
              close={this.closeBillingTargetChange}
            />
          </Row>
        )}
      </div>
    );
  }
}
CouponData.defaultProps = {
  inModal: false,
};

const mapDispatchToProps = dispatch => {
  return {
    getCouponBalance: couponId => {
      dispatch(getCouponBalance(couponId));
    },
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
  };
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    defaultTimezone: getTimezoneFromState(state),
    billingTarget: state.data.billingTarget,
    billingTargetFormStatus: state.formStatus.billingTargetEdit,
    couponBalance: state.list.couponBalance,
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(CouponData)
);
